import { authAPI } from '@/utils/api'
import { getToken, setToken, removeToken } from '@/utils/auth'

const state = {
    // 认证状态
    token: getToken(),
    isAuthenticated: false,
    authChecked: false,

    // 用户信息
    user: null,

    // 重定向信息（来自其他系统）
    redirectInfo: {
        redirect_uri: null,
        state: null,
        system: null,
        original_path: null
    },

    // 可访问的系统列表
    availableSystems: []
}

const getters = {
    isAuthenticated: state => state.isAuthenticated,
    user: state => state.user,
    token: state => state.token,
    authChecked: state => state.authChecked,
    redirectInfo: state => state.redirectInfo,
    availableSystems: state => state.availableSystems,

    // 检查用户是否有访问特定系统的权限
    hasSystemAccess: (state) => (systemId) => {
        return state.availableSystems.some(system => system.id === systemId)
    },

    // 检查是否需要刷新Token
    shouldRefreshToken: (state) => (token) => {
        if (!token) return false

        try {
            // 解析JWT Token
            const payload = JSON.parse(atob(token.split('.')[1]))
            const currentTime = Math.floor(Date.now() / 1000)
            const expirationTime = payload.exp

            // 如果Token在30分钟内过期，则需要刷新
            const refreshThreshold = 30 * 60 // 30分钟
            return (expirationTime - currentTime) < refreshThreshold
        } catch (error) {
            console.warn('Token解析失败:', error)
            return true // Token解析失败，需要刷新
        }
    }
}

const mutations = {
    // 设置Token
    SET_TOKEN(state, token) {
        state.token = token
        if (token) {
            setToken(token)
        } else {
            removeToken()
        }
    },

    // 设置认证状态
    SET_AUTHENTICATED(state, status) {
        state.isAuthenticated = status
    },

    // 设置认证检查状态
    SET_AUTH_CHECKED(state, status) {
        state.authChecked = status
    },

    // 设置用户信息
    SET_USER(state, user) {
        state.user = user
    },

    // 设置可访问系统
    SET_AVAILABLE_SYSTEMS(state, systems) {
        state.availableSystems = systems
    },

    // 设置重定向信息
    SET_REDIRECT_INFO(state, redirectInfo) {
        state.redirectInfo = { ...redirectInfo }
    },

    // 清除重定向信息
    CLEAR_REDIRECT_INFO(state) {
        state.redirectInfo = {
            redirect_uri: null,
            state: null,
            system: null,
            original_path: null
        }
    },

    // 清除认证数据
    CLEAR_AUTH_DATA(state) {
        state.token = null
        state.isAuthenticated = false
        state.user = null
        state.availableSystems = []
        removeToken()
    }
}

const actions = {
    // 用户登录
    async login({ commit, dispatch }, { username, password, rememberMe = false }) {
        try {
            console.log('🔐 开始用户登录...')

            const response = await authAPI.login({
                username,
                password,
                rememberMe
            })

            console.log('🔍 登录响应:', response)

            if (response && response.success) {
                // 保存认证信息
                commit('SET_TOKEN', response.token)
                commit('SET_USER', response.user)
                commit('SET_AVAILABLE_SYSTEMS', response.user.systems || [])
                commit('SET_AUTHENTICATED', true)
                commit('SET_AUTH_CHECKED', true)

                console.log('✅ 登录成功:', response.user.username)

                return {
                    success: true,
                    user: response.user,
                    systems: response.user.systems
                }
            } else {
                throw new Error(response.message || '登录失败')
            }
        } catch (error) {
            console.error('❌ 登录失败:', error)
            commit('CLEAR_AUTH_DATA')
            throw error
        }
    },

    // 用户注册
    async register({ commit }, userData) {
        try {
            console.log('📝 开始用户注册...')

            const response = await authAPI.register(userData)

            if (response.success) {
                console.log('✅ 注册成功')
                return {
                    success: true,
                    message: response.message
                }
            } else {
                throw new Error(response.message || '注册失败')
            }
        } catch (error) {
            console.error('❌ 注册失败:', error)
            throw error
        }
    },

    // 忘记密码
    async forgotPassword({ commit }, { email }) {
        try {
            console.log('🔑 发送密码重置邮件...')

            const response = await authAPI.forgotPassword({ email })

            if (response.success) {
                console.log('✅ 密码重置邮件发送成功')
                return {
                    success: true,
                    message: response.message
                }
            } else {
                throw new Error(response.message || '发送失败')
            }
        } catch (error) {
            console.error('❌ 密码重置失败:', error)
            throw error
        }
    },

    async getUserInfo({ commit, state }) {


        try {


            const response = await authAPI.verifyToken()

            if (response.valid) {
                commit('SET_USER', response.user)

                console.log('✅ Token有效，用户已认证')
                return true
            } else {
                throw new Error('Token无效')
            }
        } catch (error) {
            console.warn('⚠️ Token验证失败:', error.message)
            commit('CLEAR_AUTH_DATA')
            commit('SET_AUTH_CHECKED', true)
            return false
        }

    },

    // 检查认证状态
    async checkAuthStatus({ commit, state }) {
        if (state.authChecked) {
            return state.isAuthenticated
        }

        const token = state.token || getToken()

        if (!token) {
            commit('SET_AUTH_CHECKED', true)
            return false
        }

        try {
            console.log('🔍 检查认证状态...')

            const response = await authAPI.verifyToken()

            if (response.valid) {
                commit('SET_USER', response.user)
                commit('SET_AUTHENTICATED', true)
                commit('SET_AUTH_CHECKED', true)

                // 获取用户可访问的系统
                await this.dispatch('auth/fetchAvailableSystems')

                console.log('✅ Token有效，用户已认证')
                return true
            } else {
                throw new Error('Token无效')
            }
        } catch (error) {
            console.warn('⚠️ Token验证失败:', error.message)
            commit('CLEAR_AUTH_DATA')
            commit('SET_AUTH_CHECKED', true)
            return false
        }
    },

    // 获取用户可访问的系统
    async fetchAvailableSystems({ commit, state }) {
        if (!state.isAuthenticated) {
            return
        }

        try {
            const response = await authAPI.getAvailableSystems()

            if (response.success) {
                commit('SET_AVAILABLE_SYSTEMS', response.systems)
                console.log('✅ 获取可访问系统列表成功')
            }
        } catch (error) {
            console.error('❌ 获取系统列表失败:', error)
        }
    },

    // 登出
    async logout({ commit, state }) {
        try {
            console.log('🚪 用户登出...')

            // 调用后端登出API
            if (state.token) {
                await authAPI.logout()
            }

            // 清除本地状态
            commit('CLEAR_AUTH_DATA')
            commit('CLEAR_REDIRECT_INFO')

            // 清除用户模块数据
            try {
                commit('user/CLEAR_USER_DATA', null, { root: true })
            } catch (error) {
                // 忽略用户模块不存在的错误
                console.warn('用户模块清除数据失败:', error)
            }

            console.log('✅ 登出成功')

            return { success: true }
        } catch (error) {
            console.error('❌ 登出失败:', error)
            // 即使API调用失败，也要清除本地状态
            commit('CLEAR_AUTH_DATA')
            commit('CLEAR_REDIRECT_INFO')

            // 清除用户模块数据
            try {
                commit('user/CLEAR_USER_DATA', null, { root: true })
            } catch (error) {
                // 忽略用户模块不存在的错误
                console.warn('用户模块清除数据失败:', error)
            }

            return { success: true }
        }
    },

    // 刷新Token
    async refreshToken({ commit, state }) {
        try {
            if (!state.token) {
                throw new Error('没有有效的Token')
            }

            const response = await authAPI.refreshToken()

            if (response.success) {
                commit('SET_TOKEN', response.token)
                console.log('✅ Token刷新成功')
                return response.token
            } else {
                throw new Error('Token刷新失败')
            }
        } catch (error) {
            console.error('❌ Token刷新失败:', error)
            commit('CLEAR_AUTH_DATA')
            throw error
        }
    },

    // ==================== 手机号相关Actions ====================

    // 发送短信验证码
    async sendSmsCode({ commit }, { phone, purpose = 'login' }) {
        try {
            console.log(`📱 发送验证码: ${phone} (${purpose})`)

            const response = await authAPI.sendSmsCode(phone, purpose)

            if (response.success) {
                console.log('✅ 验证码发送成功')
                return {
                    success: true,
                    message: response.message,
                    expires_in: response.expires_in
                }
            } else {
                throw new Error(response.message || '验证码发送失败')
            }
        } catch (error) {
            console.error('❌ 验证码发送失败:', error)
            throw error
        }
    },

    // 手机号注册
    async registerWithPhone({ commit }, userData) {
        try {
            console.log('📝 手机号注册:', userData.phone)

            const response = await authAPI.registerWithPhone(userData)

            if (response.success) {
                console.log('✅ 手机号注册成功')
                return {
                    success: true,
                    message: response.message
                }
            } else {
                throw new Error(response.message || '注册失败')
            }
        } catch (error) {
            console.error('❌ 手机号注册失败:', error)
            throw error
        }
    },

    // 短信验证码登录
    async loginWithSms({ commit, dispatch }, { phone, verification_code, rememberMe = false }) {
        try {
            console.log('📱 短信登录:', phone)

            const response = await authAPI.loginWithSms(phone, verification_code, rememberMe)

            if (response && response.success) {
                // 保存认证信息
                commit('SET_TOKEN', response.token)
                commit('SET_USER', response.user)
                commit('SET_AVAILABLE_SYSTEMS', response.user.systems || [])
                commit('SET_AUTHENTICATED', true)
                commit('SET_AUTH_CHECKED', true)

                console.log('✅ 短信登录成功:', response.user.phone)

                return {
                    success: true,
                    user: response.user,
                    systems: response.user.systems,
                    login_type: 'sms'
                }
            } else {
                throw new Error(response.message || '短信登录失败')
            }
        } catch (error) {
            console.error('❌ 短信登录失败:', error)
            commit('CLEAR_AUTH_DATA')
            throw error
        }
    },

    // 账户登录（支持用户名或手机号 + 密码）
    async loginWithAccount({ commit, dispatch }, { account, password, rememberMe = false }) {
        try {
            console.log('🔐 账户登录:', account)

            const response = await authAPI.loginWithAccount(account, password, rememberMe)

            if (response && response.success) {
                // 保存认证信息
                commit('SET_TOKEN', response.token)
                commit('SET_USER', response.user)
                commit('SET_AVAILABLE_SYSTEMS', response.user.systems || [])
                commit('SET_AUTHENTICATED', true)
                commit('SET_AUTH_CHECKED', true)

                console.log('✅ 账户登录成功:', response.user.username)

                return {
                    success: true,
                    user: response.user,
                    systems: response.user.systems,
                    login_type: 'account'
                }
            } else {
                throw new Error(response.message || '登录失败')
            }
        } catch (error) {
            console.error('❌ 账户登录失败:', error)
            commit('CLEAR_AUTH_DATA')
            throw error
        }
    },

    // 重置密码
    async resetPassword({ commit }, { phone, verification_code, new_password }) {
        try {
            console.log('🔑 重置密码:', phone)

            const response = await authAPI.resetPassword(phone, verification_code, new_password)

            if (response.success) {
                console.log('✅ 密码重置成功')
                return {
                    success: true,
                    message: response.message
                }
            } else {
                throw new Error(response.message || '密码重置失败')
            }
        } catch (error) {
            console.error('❌ 密码重置失败:', error)
            throw error
        }
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
} 